<template>
  <div class="content-display">
    <div v-if="block.content_type === 'text-block' && block.textBlock">
      <div :class="[block.textBlock.paragraphStyle, block.textBlock.headingStyle, 'text-content']">{{ block.textBlock.content }}</div>
    </div>
    <div v-if="block.content_type === 'media-block' && block.mediaBlock">
      <div v-if="block.mediaBlock.mediaType === 'column-gallery'" class="images" :class="{double: block.mediaBlock.columnCount === 2, triple: block.mediaBlock.columnCount === 3}">
        <div v-for="(image, index) in block.mediaBlock.images" :key="index" class="image-preview">
          <!-- Vimeo Video -->
          <div v-if="isVimeo(image.image)" class="vimeo-wrapper">
            <div :ref="setVimeoRef(index)"></div>
            <img v-if="!vimeoPlayers[index] && vimeoPreviewImages[index]" :src="vimeoPreviewImages[index]"
              class="vimeo-preview" :alt="image.caption" />
          </div>
          <!-- Local Video -->
          <video v-else-if="isVideo(image.image)" autoplay muted loop playsinline :src="image.image" class="video-preview" :key="`video-${index}-${image.image}`" :ref="setVideoRef(index)" />
          <!-- Regular Image -->
          <img v-else :src="image.image" :alt="image.caption" />
          <div class="caption" v-if="image.caption">{{image.caption}}</div>
        </div>
      </div>
      <div v-else-if="block.mediaBlock.mediaType === 'justify-gallery'" class="justify-gallery">
        <div v-for="(image, index) in block.mediaBlock.images" :key="index" class="gallery-item" :style="getGridStyle(image)">
          <!-- Vimeo Video -->
          <div v-if="isVimeo(image.image)" class="vimeo-wrapper">
            <div :ref="setVimeoRef(index)"></div>
            <img v-if="!vimeoPlayers[index] && vimeoPreviewImages[index]" :src="vimeoPreviewImages[index]"
              class="vimeo-preview" :alt="image.caption" />
          </div>
          <!-- Local Video -->
          <video v-else-if="isVideo(image.image)" autoplay muted loop playsinline :src="image.image" class="video-preview" :key="`video-${index}-${image.image}`" :ref="setVideoRef(index)" />
          <!-- Regular Image -->
          <img v-else :src="image.image" :alt="image.caption" />
          <div class="caption" v-if="image.caption">{{image.caption}}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
import axios from 'axios';
// @ts-ignore: No types for @vimeo/player
import Player from '@vimeo/player';

interface TextBlock {
  paragraphStyle: string;
  headingStyle: string;
  content: string;
}

interface MediaBlock {
  mediaType: string;
  columnCount?: number;
  images: any[];
}

interface ContentBlock {
  content_type: string;
  textBlock?: TextBlock;
  mediaBlock?: MediaBlock;
}

const props = defineProps<{ block: ContentBlock }>();

// Video and Vimeo handling
const vimeoRefs = ref<any[]>([]);
const vimeoPlayers = ref<any[]>([]);
const vimeoPreviewImages = ref<string[]>([]);
const videoRefs = ref<any[]>([]);
const VIMEO_ACCESS_TOKEN = '2882f8d876e951dfb0357c88629dcf78';

// Helper functions
const isVimeo = (url: string) => {
  return url && url.includes('vimeo.com');
};

const isVideo = (url: string) => {
  return url && (url.includes('.mp4') || url.includes('.mov'));
};

const getVimeoVideoId = (url: string) => {
  const regex = /vimeo\.com\/(?:.*#|.*\/videos\/)?([0-9]+)(?:$|\/|\?)/;
  const match = regex.exec(url);
  return match ? match[1] : null;
};

const getStartEndParams = (url: string): { start?: number; end?: number } => {
  try {
    const parsedUrl = new URL(url);
    const params = parsedUrl.searchParams;
    
    const start = params.get('start');
    const end = params.get('end');
    
    return {
      start: start ? parseInt(start, 10) : undefined,
      end: end ? parseInt(end, 10) : undefined,
    };
  } catch (error) {
    return {};
  }
};

const fetchVimeoVideoData = async (videoUrl: string, accessToken: string) => {
  const videoId = getVimeoVideoId(videoUrl);
  if (!videoId) return null;
  
  try {
    const response = await axios.get(`https://api.vimeo.com/videos/${videoId}`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    const data = response.data;
    const previewImage = data.pictures?.sizes?.[0]?.link || '';
    const width = data.width || 16;
    const height = data.height || 9;
    return { previewImage, width, height };
  } catch (error) {
    console.error('Vimeo API error:', error);
    return null;
  }
};

const setVimeoRef = (index: number) => (el: any) => {
  if (el) {
    vimeoRefs.value[index] = el;
  } else {
    delete vimeoRefs.value[index];
  }
};

const setVideoRef = (index: number) => (el: any) => {
  if (el) {
    videoRefs.value[index] = el;
    // Simple setup - just get start/end and apply them
    const image = props.block.mediaBlock?.images[index];
    if (image) {
      const { start, end } = getStartEndParams(image.image);
      
      // Always muted
      el.muted = true;
      
      // Set start time when video loads
      if (start !== undefined) {
        el.addEventListener('loadeddata', () => {
          el.currentTime = start;
          el.muted = true;
        }, { once: true });
      }
      
      // Handle end time looping
      if (end !== undefined && start !== undefined) {
        el.addEventListener('timeupdate', () => {
          if (el.currentTime >= end) {
            el.currentTime = start;
          }
        });
      }
      
      // Keep muted
      el.addEventListener('play', () => {
        el.muted = true;
      });
    }
  } else {
    delete videoRefs.value[index];
  }
};

const initializeVimeoPlayers = async () => {
  if (!props.block?.mediaBlock?.images) return;
  
  for (let index = 0; index < props.block.mediaBlock.images.length; index++) {
    const image = props.block.mediaBlock.images[index];
    
    if (isVimeo(image.image)) {
      try {
        const videoData = await fetchVimeoVideoData(image.image, VIMEO_ACCESS_TOKEN);
        if (videoData) {
          vimeoPreviewImages.value[index] = videoData.previewImage;
        }
        
        await nextTick();
        
        if (vimeoRefs.value[index] && !vimeoPlayers.value[index]) {
          const videoId = getVimeoVideoId(image.image);
          if (videoId) {
            const { start, end } = getStartEndParams(image.image);
            
            // Wait for DOM to be fully rendered before getting dimensions
            await nextTick();
            
            // Additional small delay for layout to settle
            await new Promise(resolve => setTimeout(resolve, 150));
            
            // Get parent container dimensions
            const parentElement = vimeoRefs.value[index].parentElement;
            const parentWidth = parentElement ? parentElement.clientWidth : 640;
            const parentHeight = parentElement ? parentElement.clientHeight : 360;
            console.log('parentWidth', parentWidth, parentHeight, parentElement);
            
            const player = new Player(vimeoRefs.value[index], {
              id: parseInt(videoId),
              autoplay: true,
              muted: true,
              loop: true,
              autopause: false,
              controls: false,
              width: parentWidth
            });
            
            vimeoPlayers.value[index] = player;
            
            player.on('loaded', async () => {
              // Set start time if specified
              if (start !== undefined) {
                await player.setCurrentTime(start);
              }
              
              // Handle end time by looping back to start
              if (end !== undefined && start !== undefined) {
                player.on('timeupdate', async (data: any) => {
                  if (data.seconds >= end) {
                    await player.setCurrentTime(start);
                  }
                });
              }
            });
            
            player.on('error', (err: any) => {
              console.error('Vimeo player error:', err);
            });
          }
        }
      } catch (error) {
        console.error('Error initializing Vimeo player:', error);
      }
    }
  }
};

const getGridStyle = (image: any) => {
  const isHorizontal = image.isHorizontal;
  return {
    gridColumn: isHorizontal ? 'span 2' : 'span 1',
    gridRow: 'span 1',
  };
};

onMounted(async () => {
  if (process.client) {
    await initializeVimeoPlayers();
  }
});

onUnmounted(() => {
  // Clean up Vimeo players
  vimeoPlayers.value.forEach((player: any) => {
    if (player && typeof player.destroy === 'function') {
      try {
        const res = player.destroy();
        if (res && typeof res.then === 'function') {
          res.catch(() => {});
        }
      } catch (e) {
        // ignore
      }
    }
  });
  vimeoPlayers.value = [];
  
  // Clean up video refs
  videoRefs.value = [];
});

// Watch for changes to reinitialize players if needed, but prevent unnecessary re-initialization
watch(() => props.block?.mediaBlock?.images, async (newImages, oldImages) => {
  if (process.client && newImages) {
    // Only reinitialize if the actual Vimeo URLs have changed
    const newVimeoUrls = newImages.filter(img => isVimeo(img.image)).map(img => img.image);
    const oldVimeoUrls = oldImages ? oldImages.filter(img => isVimeo(img.image)).map(img => img.image) : [];
    
    // Check if Vimeo URLs have actually changed
    const urlsChanged = JSON.stringify(newVimeoUrls) !== JSON.stringify(oldVimeoUrls);
    
    if (urlsChanged) {
      // Clean up existing players before reinitializing
      vimeoPlayers.value.forEach((player: any) => {
        if (player && typeof player.destroy === 'function') {
          try {
            player.destroy();
          } catch (e) {
            // ignore
          }
        }
      });
      vimeoPlayers.value = [];
      
      await initializeVimeoPlayers();
    }
  }
}, { deep: true });
</script>

<style scoped>
/* Styles for the content display component */

.text-content {
  padding: 20px 0;
  color: black;
  white-space: pre-wrap !important; /* Preserve line breaks from saved content */
}

.images {
  display: flex;
  padding: 20px 0;
}
.images.double {
  gap: 28px;
}
.images.triple {
  gap: 19px;
}

.image-preview {
  width: 50%;
  padding: 0;
  flex: 1;
}

.image-preview img,
.video-preview,
.vimeo-wrapper {
  width: 100%;
  height: auto;
  object-fit: cover;
}

.vimeo-wrapper {
  position: relative;
  overflow: hidden;
}

.vimeo-wrapper iframe {
  width: 100%;
  height: 100%;
}

.vimeo-preview {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.justify-gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  padding: 20px;
}

.justify-gallery .gallery-item img,
.justify-gallery .gallery-item .video-preview,
.justify-gallery .gallery-item .vimeo-wrapper {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.heading-1 {
  font-size: 18px;
  font-weight: 400;
  color: black;
  line-height: 1.25;
}

.heading-2 {
  font-size: 15px;
  font-weight: 400;
  color: #b8b8b8;
  line-height: 1.25;
}

.heading-3 {

  font-size: 32px;
  font-weight: 400;
  color: black;
  line-height: 1.25;
  margin-bottom: 40px;
}

.paragraph-1 {
  padding-left: 0 !important;
}

.paragraph-2 {
  padding-left: 150px !important;
}

.paragraph-3 {
  column-count: 2;
  column-gap: 20px; /* Adjust the gap between columns as needed */
  padding-left: 0 !important; /* Ensure consistent padding */
  text-align: start; /* Optional: Justify text for a cleaner look */
}
.caption {
  font-size: 12px;
  line-height: 15px;
  padding-top: 15px;
}

/* Mobile styles (min-width: 320px to max-width: 479px) */
@media screen and (max-width: 768px) {
  .text-content {
    padding: 0;
  }
  .paragraph-2 {
    padding-left: 30px !important;
  }
  .heading-3 {
    font-size: 14px !important;
  }
  .heading-1 {
    font-size: 12px !important;
  }
  .heading-2 {
    font-size: 10px !important;
  }
  .caption {
    font-size: 10px !important;
  }
  .images.triple {
    gap: 5px !important;
  }
  .images.double {
    gap: 10px !important;
  }
  .image-preview {
    width: 100% !important;
  }
}
</style>
